import { useEffect, useRef } from "react";

export type RealtimeEvent =
  | { type: "visitor.updated"; visitor: any }
  | { type: "order.updated"; order: any }
  | { type: "order.created"; order: any }
  | { type: "operator.presence"; userId: string; presence: "online" | "offline" }
  | { type: "queue.snapshot"; counts: Record<string, number> };

/**
 * Подписывается на одну "комнату" realtime-хаба (Durable Object). При обрыве соединения
 * переподключается с экспоненциальной задержкой (1с, 2с, 4с... максимум 15с) — это и есть
 * защита от "клиент должен постоянно обновлять страницу" из ТЗ: обновление придёт само,
 * как только соединение восстановится, а на момент восстановления backend остаётся
 * источником истины (после reconnect можно дополнительно перезапросить состояние явно).
 */
export function useRealtime(room: string | null, onEvent: (event: RealtimeEvent) => void) {
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!room) return;
    const activeRoom = room;
    let socket: WebSocket | null = null;
    let closedByUs = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    function connect() {
      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      socket = new WebSocket(`${proto}://${window.location.host}/realtime?room=${encodeURIComponent(activeRoom)}`);
      socket.onopen = () => {
        attempt = 0;
      };
      socket.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data) as RealtimeEvent;
          onEventRef.current(data);
        } catch {
          /* игнорируем некорректный payload */
        }
      };
      socket.onclose = () => {
        if (closedByUs) return;
        const delay = Math.min(15_000, 1000 * 2 ** attempt);
        attempt += 1;
        timer = setTimeout(connect, delay);
      };
      socket.onerror = () => {
        socket?.close();
      };
    }

    connect();
    return () => {
      closedByUs = true;
      if (timer) clearTimeout(timer);
      socket?.close();
    };
  }, [room]);
}